import React from 'react';
import { ActivityEntry } from '../../types/activity';
import { Badge } from '../ui/Badge';
import { FileText, EyeOff, ArrowRight } from 'lucide-react';

interface ActivityItemProps {
  entry: ActivityEntry;
}

const TYPE_CONFIG: Record<string, { label: string; variant: 'info' | 'warning' | 'success' | 'danger' | 'neutral' }> = {
  comment: { label: 'Comment', variant: 'info' },
  status_change: { label: 'Status', variant: 'warning' },
  assignment: { label: 'Assignment', variant: 'info' },
  priority_change: { label: 'Priority', variant: 'danger' },
  attachment: { label: 'Attachment', variant: 'neutral' },
  internal_note: { label: 'Internal Note', variant: 'warning' },
};

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function highlightMentions(text: string, mentions: { user_name: string }[]): React.ReactNode {
  if (mentions.length === 0) return text;
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  for (const mention of mentions) {
    const pattern = `@${mention.user_name}`;
    const idx = remaining.indexOf(pattern);
    if (idx === -1) continue;
    if (idx > 0) parts.push(<span key={key++}>{remaining.slice(0, idx)}</span>);
    parts.push(
      <span key={key++} className="text-sky-400 font-semibold bg-sky-500/10 px-1 rounded">
        {pattern}
      </span>
    );
    remaining = remaining.slice(idx + pattern.length);
  }
  if (remaining) parts.push(<span key={key++}>{remaining}</span>);
  return parts.length > 0 ? parts : text;
}

export const ActivityItem: React.FC<ActivityItemProps> = ({ entry }) => {
  const config = TYPE_CONFIG[entry.activity_type] || TYPE_CONFIG.comment;

  if (entry.activity_type === 'status_change' || entry.activity_type === 'assignment' || entry.activity_type === 'priority_change') {
    return (
      <div className="flex items-start gap-3 py-2">
        <div className="w-6 h-6 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center flex-shrink-0 mt-0.5">
          <ArrowRight className="w-3 h-3 text-slate-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-400">{entry.author_name}</span>
            <Badge label={config.label} variant={config.variant} />
            <span className="text-[10px] text-slate-600">{formatTimestamp(entry.created_at)}</span>
          </div>
          <p className="text-xs text-slate-300 mt-0.5">{entry.content}</p>
          {entry.system_meta?.field && entry.system_meta.old_value && entry.system_meta.new_value && (
            <div className="flex items-center gap-1.5 mt-1 text-[10px]">
              <span className="text-slate-500">{entry.system_meta.old_value}</span>
              <ArrowRight className="w-3 h-3 text-slate-600" />
              <span className="text-sky-400 font-medium">{entry.system_meta.new_value}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-start gap-3 py-2 ${entry.is_internal ? 'border-l-2 border-amber-500/40 pl-3' : ''}`}>
      <div className="w-7 h-7 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center flex-shrink-0 mt-0.5">
        <span className="text-[10px] font-semibold text-slate-300">
          {entry.author_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-slate-200">{entry.author_name}</span>
          <span className="text-[10px] text-slate-500 px-1.5 py-0.5 bg-slate-800 rounded-full">{entry.author_role}</span>
          {entry.is_internal && (
            <span className="flex items-center gap-0.5 text-[10px] text-amber-400">
              <EyeOff className="w-3 h-3" /> Internal
            </span>
          )}
          <span className="text-[10px] text-slate-600">{formatTimestamp(entry.created_at)}</span>
        </div>
        <p className="text-xs text-slate-300 mt-1 leading-relaxed">
          {highlightMentions(entry.content, entry.mentions)}
        </p>
        {entry.attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {entry.attachments.map(att => (
              <div key={att.id} className="flex items-center gap-1.5 bg-slate-800 border border-slate-700 rounded-lg px-2 py-1">
                <FileText className="w-3 h-3 text-slate-400" />
                <span className="text-[10px] text-slate-400 truncate max-w-[120px]">{att.file_name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
